import React, { FC, useEffect, useMemo, useState } from 'react'
import { KTSVG } from '../../../../_metronic/helpers'
import { getGeneralInfoOfVessel, getVesselList, getVesselParticulars } from '../core/_requests'
import { Vessel } from '../core/_models'
import AddVesselModal from './AddVesselModal'
import EditVesselModal from './EditVesselModal'
import ViewVesselModal from './ViewVesselModal'
import DeleteVesselModal from './DeleteVesselModal'
import AddAdditionalInfoModal from './AddAdditionalInfoModal'
import ShipParticularsModal from './ShipParticularsModal'
import VesselLandingModal from './VesselLanding'
import VesselLandingLink from '../../../shared/components/VesselLandingLink'
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import * as XLSX from 'xlsx';
import { showErrorToast } from '../../../shared/utils/apiErrorMessage';
import { useAuth } from '../../auth'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { vesselGates } from '../core/adminPermissions'
import { extractRows } from '../../../shared/access/tenantScopeMatch'
import { actorCanPickAnyCompanyGroup } from '../core/adminActorContext'
import { useGlobalFilter } from '../../../shared/context/GlobalFilterContext'
import {
  adminListEmptyMessage,
  initialAdminListAccessState,
  resolveAdminListFetchError,
  type AdminListAccessState,
} from '../core/adminListAccess'
import {getVesselDetailLabel, getVesselDetailMode} from '../core/vesselDetailMode'
import ViewShipParticularsModal from './ViewShipParticularsModal'
import SearchableSelect from '../../../shared/components/SearchableSelect'

const hasSavedDetails = (payload: Record<string, any> | null | undefined) =>
  Boolean(
    payload &&
      Object.entries(payload).some(
        ([key, value]) =>
          key !== 'vesselId' &&
          value !== null &&
          value !== undefined &&
          !(typeof value === 'string' && value.trim() === '')
      )
  )

const VesselsList: FC = () => {
    const [vessels, setVessels] = useState<Vessel[]>([]);
    const [filteredVessels, setFilteredVessels] = useState<Vessel[]>([]);
    const [selectedVessel, setSelectedVessel] = useState<Vessel>();
    const [infoExists, setInfoExists] = useState<Record<number, boolean>>({})
    const [isEditMode, setIsEditMode] = useState(false)

    const [isAddVesselModalOpen, setIsAddVesselModalOpen] = useState(false)
    const [isEditVesselModalOpen, setIsEditVesselModalOpen] = useState(false)
    const [isViewVesselModalOpen, setIsViewVesselModalOpen] = useState(false)
    const [isDeleteVesselModalOpen, setIsDeleteVesselModalOpen] = useState(false)
    const [isAddAdditionalInfoModalOpen, setIsAddAdditionalInfoModalOpen] = useState(false)

    const [landingVesselId, setLandingVesselId] = useState<number | null>(null)
    const [openedLandingFromQuery, setOpenedLandingFromQuery] = useState(false)

    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState<{ key: keyof Vessel | null, direction: 'asc' | 'desc' }>({
        key: null,
        direction: 'asc',
    });

    const { currentUser, hasPermission, isSuperAdmin } = useAuth()
    const { state: globalState } = useGlobalFilter()
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const showCompanyGroupColumn = useMemo(
      () => actorCanPickAnyCompanyGroup(currentUser),
      [currentUser]
    )
    const actionGates = useMemo(
      () => vesselGates(hasPermission, isSuperAdmin),
      [hasPermission, isSuperAdmin]
    )
    const canExportVessels = isSuperAdmin || hasPermission('DOWNLOAD_VESSEL')
    const [listAccess, setListAccess] = useState<AdminListAccessState>(initialAdminListAccessState)

    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const totalPages = Math.ceil(filteredVessels.length / rowsPerPage);
    const indexOfLastVessel = currentPage * rowsPerPage;
    const indexOfFirstVessel = indexOfLastVessel - rowsPerPage;
    const currentVessels = filteredVessels.slice(indexOfFirstVessel, Math.min(indexOfLastVessel, filteredVessels.length));

    useEffect(() => {
        const landingParam = searchParams.get('landingVesselId')
        if (!landingParam) return
        const vesselId = Number(landingParam)
        if (!Number.isFinite(vesselId)) return
        if (vessels.length === 0) return
        const vessel = vessels.find((item) => Number(item.id) === vesselId)
        if (!vessel) return
        setLandingVesselId(vesselId)
        setOpenedLandingFromQuery(true)
    }, [searchParams, vessels])

    // Fetch vessel list
    const fetchVessels = async () => {
        if (!actionGates.canRead) {
            setListAccess({ loading: false, accessDenied: true, loadError: null })
            setVessels([])
            setFilteredVessels([])
            return
        }
        setListAccess((s) => ({ ...s, loading: true, accessDenied: false, loadError: null }))
        try {
            const vesselList = extractRows<Vessel>(await getVesselList());
            setVessels(vesselList);
            setListAccess({ loading: false, accessDenied: false, loadError: null })
        } catch (error) {
            console.error('Failed to fetch vessel list:', error);
            setVessels([])
            setListAccess(resolveAdminListFetchError(error, 'Failed to load vessels'))
        }
    };


    // After fetching vessels, check which have additional info
    useEffect(() => {
        vessels.forEach(v => {
            const loader =
              getVesselDetailMode(v.vesselType) === 'Q88'
                ? getGeneralInfoOfVessel(v.id)
                : getVesselParticulars(v.id)
            loader
                .then((payload: any) => setInfoExists(prev => ({ ...prev, [v.id]: hasSavedDetails(payload) })))
                .catch(() => setInfoExists(prev => ({ ...prev, [v.id]: false })))
        })
    }, [vessels])

    // Initial load
    useEffect(() => {
        fetchVessels()
    }, [])

    useEffect(() => {
        const refresh = () => { void fetchVessels() }
        window.addEventListener('rbac-permissions-updated', refresh)
        return () => window.removeEventListener('rbac-permissions-updated', refresh)
    }, [])



    useEffect(() => {
  let filtered = actionGates.canRead
    ? vessels.filter(vessel => {
        const matchesSearch = vessel.fleet_name?.toLowerCase().includes(searchTerm.toLowerCase())
        const isActive = vessel.active

        // Global filter: company
        let matchesCompany = true
        if (globalState.company) {
          if (globalState.company.type === 'company_group') {
            matchesCompany =
              (vessel as any).companyGroupAdmin?.id === globalState.company.id ||
              (vessel as any).companyGroupId === globalState.company.id
          } else {
            matchesCompany =
              (vessel as any).companyAdmin?.id === globalState.company.id ||
              (vessel as any).companyId === globalState.company.id
          }
        }

        // Global filter: vessel
        let matchesVessel = true
        if (globalState.vessel) {
          matchesVessel = vessel.id === globalState.vessel.id
        }

        return matchesSearch && isActive && matchesCompany && matchesVessel
      })
    : []

  if (sortConfig.key) {
    filtered = [...filtered].sort((a, b) => {
      const aValue = (a[sortConfig.key!] ?? '').toString().toLowerCase()
      const bValue = (b[sortConfig.key!] ?? '').toString().toLowerCase()
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1
      return 0
    })
  }

  setFilteredVessels(filtered)
  setCurrentPage(1)
}, [searchTerm, vessels, sortConfig, actionGates.canRead, globalState.company, globalState.vessel])

    const handleSort = (key: keyof Vessel) => {
        setSortConfig((prev) => {
            if (prev.key === key) {
                return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
            } else {
                return { key, direction: 'asc' };
            }
        });
    };


    // useEffect(() => {
    //     const filtered = vessels.filter(vessel => {
    //         const matchesSearch = vessel.fleet_name?.toLowerCase().includes(searchTerm.toLowerCase());
    //         const isActive = vessel.active;

    //         // Apply role-based filtering only if roleId is 5 or 2
    //         const isRoleRelevant = roleId === 5 || roleId === 2;
    //         const roleFilter = !isRoleRelevant || (
    //             (roleId === 5 && vessel.companyGroupAdmin?.id === roleEntityId) ||
    //             (roleId === 2 && vessel.companyAdmin?.id === roleEntityId)
    //         );

    //         return matchesSearch && isActive && roleFilter;
    //     });

    //     setFilteredVessels(filtered);
    //     setCurrentPage(1);
    // }, [searchTerm, vessels, roleId, roleEntityId]);

    useEffect(() => {
        if (currentPage > totalPages) {
            setCurrentPage(1); // Reset to first page if the current page exceeds total pages
        }
    }, [filteredVessels, rowsPerPage]);



    const handleExcelDownload = () => {
        try {
            if (!filteredVessels || filteredVessels.length === 0) {
                toast.warn('No vessel data available to export.');
                return;
            }

            const exportData = filteredVessels.map((v) => ({
                'Vessel Name': v.fleet_name || '',
                'IMO Number': v.imoNumber || '',
                'Official Number': v.officialNumber || '',
                'Vessel Type': v.vesselType || '',
                'MMSI': v.mmsi || '',
                'Call Sign': v.call_sign || '',
                'Flag': v.flag || '',
                'Port of Registry': v.portOfRegistry || '',
                'Class': v.classes || '',
                'Class Notation': v.classNotation || '',
                'P&I Club': v.piClub || '',
                'Trading Area': v.area || '',
                'DWT': v.dwt || '',
                'Company Group': v.companyGroupAdmin?.name || '',
                'Sub-Company': v.companyAdmin?.name || '',
                'Status': v.active ? 'Active' : 'Inactive',
            }));

            const worksheet = XLSX.utils.json_to_sheet(exportData);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Vessels');
            XLSX.writeFile(workbook, 'vessels.xlsx');
            toast.success(`Exported ${filteredVessels.length} vessels to Excel.`);
        } catch (err) {
            console.error('Failed to export vessels to Excel:', err);
            showErrorToast(err, 'Failed to export vessels to Excel.');
        }
    };

    
    const landingVessel = landingVesselId !== null
        ? vessels.find(v => v.id === landingVesselId) || null
        : null

    return (
        <div
            className='app-main flex-column flex-row-fluid'
            id='kt_app_main'
        >
            <div className='d-flex flex-column flex-column-fluid'>
                <div
                    id='kt_app_content'
                    className='app-content flex-column-fluid d-flex flex-column'
                >
                    <div className='card flex-column-fluid d-flex flex-column bg-white border-top' style={{ flex: 1 }}>
                        {/* Header */}
                        <div className='d-flex gap-3 pe-5'>
                            <div className='py-3 ms-3' style={{ flex: 5 }}>
                                <div className='d-flex align-items-center gap-3 mb-5'>
                                    <h3 className='card-title fw-bold text-dark mb-0'>Vessels</h3>
                                    {listAccess.loading && (
                                        <span className='badge badge-light-primary d-inline-flex align-items-center gap-2 py-2 px-3 fs-7 fw-normal'>
                                            <span className='spinner-border spinner-border-sm text-primary' role='status' aria-hidden='true'></span>
                                            Loading vessels...
                                        </span>
                                    )}
                                </div>
                                <div className='d-flex justify-content-between mb-5'>
                                    <div className='d-flex align-items-center gap-2'>
                                        <input
                                            type="text"
                                            className="form-control cp_search_input"
                                            placeholder="Vessel Name"
                                            value={searchTerm}
                                            onChange={(e) => {
                                                setSearchTerm(e.target.value);
                                            }}
                                        />
                                    </div>
                                    <div className='d-flex align-items-center gap-3'>
                                        {canExportVessels && (
                                          <button
                                            type="button"
                                            className='btn text-white d-inline-flex align-items-center justify-content-center'
                                            style={{
                                              backgroundColor: '#334155',
                                              borderRadius: '9.75px',
                                              padding: '8px 13px',
                                              fontSize: '11.5px',
                                              fontWeight: 700,
                                              gap: '6.5px',
                                              boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
                                              transition: 'all 0.2s ease',
                                              border: 'none',
                                              height: '36px'
                                            }}
                                            onClick={handleExcelDownload}
                                            title="Export to Excel"
                                          >
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                                              <polyline points="7 10 12 15 17 10"/>
                                              <line x1="12" y1="15" x2="12" y2="3"/>
                                            </svg>
                                            Export Excel
                                          </button>
                                        )}
                                        {actionGates.canCreate && (
                                          <button
                                            type="button"
                                            className='btn btn-primary text-white d-inline-flex align-items-center justify-content-center'
                                            style={{
                                              borderRadius: '9.75px',
                                              padding: '8px 13px',
                                              fontSize: '11.5px',
                                              fontWeight: 700,
                                              gap: '6.5px',
                                              boxShadow: '0 2px 4px rgba(37,99,235,0.2)',
                                              transition: 'all 0.2s ease',
                                              border: 'none',
                                              height: '36px'
                                            }}
                                            onClick={() => setIsAddVesselModalOpen(true)}
                                          >
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                              <line x1="12" y1="5" x2="12" y2="19" />
                                              <line x1="5" y1="12" x2="19" y2="12" />
                                            </svg>
                                            Add Vessel
                                          </button>
                                        )}
                                    </div>
                                </div>

                                {/* Notice / Error Banner */}
                                {listAccess.loadError && (
                                    <div className='alert alert-danger d-flex align-items-center justify-content-between p-3 mb-4 rounded-3 border border-danger-subtle'>
                                        <div className='d-flex align-items-center gap-3'>
                                            <i className='bi bi-exclamation-triangle-fill fs-3 text-danger' />
                                            <div>
                                                <div className='fw-bold text-danger fs-7'>Failed to Load Vessels</div>
                                                <div className='text-dark fs-8'>{listAccess.loadError}</div>
                                            </div>
                                        </div>
                                        <button
                                            className='btn btn-sm btn-outline-danger d-flex align-items-center gap-1 px-3 py-1 text-nowrap'
                                            onClick={fetchVessels}
                                            disabled={listAccess.loading}
                                        >
                                            <i className={`bi bi-arrow-clockwise ${listAccess.loading ? 'spinner-border spinner-border-sm' : ''}`} />
                                            {listAccess.loading ? 'Retrying...' : 'Retry'}
                                        </button>
                                    </div>
                                )}
                                {listAccess.accessDenied && (
                                    <div className='alert alert-warning d-flex align-items-center p-3 mb-4 rounded-3 border border-warning-subtle'>
                                        <i className='bi bi-shield-lock-fill fs-3 text-warning me-3' />
                                        <div>
                                            <div className='fw-bold text-warning fs-7'>Access Restricted</div>
                                            <div className='text-muted fs-8'>You do not have permission to view vessels. Contact your administrator if you need access.</div>
                                        </div>
                                    </div>
                                )}

                                {/* Vessels Table */}
                                <div className='pt-3'>
                                    <div className="report-table table-responsive">
                                        <table className="table table-bordered align-middle">
                                            <thead className="table-header py-5">
                                                <tr>
                                                    <th onClick={() => handleSort('fleet_name')} className="cursor-pointer">
                                                        VESSEL NAME
                                                        <KTSVG
                                                            path="/media/map/report_table_filter.svg"
                                                            className={`svg-icon-small ms-2 ${sortConfig.key === 'fleet_name' && sortConfig.direction === 'asc' ? 'rotate-180' : ''}`}
                                                        />
                                                    </th>
                                                    <th onClick={() => handleSort('fleet_name')} className="cursor-pointer">
                                                        IMO NUMBER
                                                        <KTSVG
                                                            path="/media/map/report_table_filter.svg"
                                                            className={`svg-icon-small ms-2 ${sortConfig.key === 'fleet_name' && sortConfig.direction === 'asc' ? 'rotate-180' : ''}`}
                                                        />
                                                    </th>
                                                    <th onClick={() => handleSort('fleet_name')} className="cursor-pointer">
                                                        VESSEL TYPE
                                                        <KTSVG
                                                            path="/media/map/report_table_filter.svg"
                                                            className={`svg-icon-small ms-2 ${sortConfig.key === 'fleet_name' && sortConfig.direction === 'asc' ? 'rotate-180' : ''}`}
                                                        />
                                                    </th>
                                                    <th onClick={() => handleSort('fleet_name')} style={{ cursor: 'pointer' }}>
                                                        COMPANY NAME
                                                        <KTSVG path="/media/map/report_table_filter.svg" className="svg-icon-small ms-2" />
                                                    </th>
                                                    <th onClick={() => handleSort('fleet_name')} style={{ cursor: 'pointer' }}>
                                                        SUB-COMPANY NAME
                                                        <KTSVG path="/media/map/report_table_filter.svg" className="svg-icon-small ms-2" />
                                                    </th>
                                                    {<th>ACTIONS</th>}
                                                    {/* <th>ADDITIONAL DETAILS</th> */}
                                                </tr>
                                            </thead>
                                            <tbody className="table-body">
                                                {listAccess.loading ? (
                                                    Array.from({ length: 8 }).map((_, i) => (
                                                        <tr key={`vessel-sk-${i}`}>
                                                            <td className='py-3'>
                                                                <div className='placeholder-glow'>
                                                                    <span className='placeholder col-8 rounded' style={{ height: 16 }}></span>
                                                                </div>
                                                            </td>
                                                            <td className='py-3'>
                                                                <div className='placeholder-glow'>
                                                                    <span className='placeholder col-7 rounded' style={{ height: 16 }}></span>
                                                                </div>
                                                            </td>
                                                            <td className='py-3'>
                                                                <div className='placeholder-glow'>
                                                                    <span className='placeholder col-8 rounded' style={{ height: 16 }}></span>
                                                                </div>
                                                            </td>
                                                            <td className='py-3'>
                                                                <div className='placeholder-glow'>
                                                                    <span className='placeholder col-9 rounded' style={{ height: 16 }}></span>
                                                                </div>
                                                            </td>
                                                            <td className='py-3'>
                                                                <div className='placeholder-glow'>
                                                                    <span className='placeholder col-8 rounded' style={{ height: 16 }}></span>
                                                                </div>
                                                            </td>
                                                            <td className='py-3'>
                                                                <div className='placeholder-glow'>
                                                                    <span className='placeholder col-8 rounded' style={{ height: 24 }}></span>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : currentVessels.length > 0 ? (
                                                    currentVessels.map((vessel, idx) => (
                                                        <tr key={idx}>
                                                             <td>
                                                            {vessel.fleet_name ? (
                                                                <VesselLandingLink vesselId={vessel.id} vesselName={vessel.fleet_name} />
                                                            ) : ('-')}
                                                        </td>
                                                            <td>{vessel.imoNumber ?? '-'}</td>
                                                            <td>{vessel.vesselType ?? '-'}</td>
                                                            <td>{vessel.companyGroupAdmin?.name ?? '-'}</td>
                                                            <td>{vessel.companyAdmin?.name ?? '-'}</td>

                                                            <td>
                                                                {actionGates.canUpdate && (
                                                                <>
                                                                <button className="btn btn-sm px-0" onClick={() => { setIsEditVesselModalOpen(true); setSelectedVessel(vessel); }}>
                                                                    <KTSVG path='/media/map/edit-active.svg' className='' />
                                                                </button>
                                                                    {infoExists[vessel.id] ? (
                                                                        <button
                                                                            className="btn btn-sm px-0"
                                                                            title={getVesselDetailLabel(vessel.vesselType, 'fill')}
                                                                            aria-label={getVesselDetailLabel(vessel.vesselType, 'fill')}
                                                                            onClick={() => {
                                                                                setSelectedVessel(vessel)
                                                                                setIsEditMode(true)
                                                                                setIsAddAdditionalInfoModalOpen(true)
                                                                            }}
                                                                        >
                                                                            <KTSVG path="/media/map/square-plus.svg" className="" />
                                                                        </button>
                                                                    ) : (
                                                                        <button
                                                                            className="btn btn-sm px-0"
                                                                            title={getVesselDetailLabel(vessel.vesselType, 'fill')}
                                                                            aria-label={getVesselDetailLabel(vessel.vesselType, 'fill')}
                                                                            onClick={() => {
                                                                                setSelectedVessel(vessel)
                                                                                setIsEditMode(false)
                                                                                setIsAddAdditionalInfoModalOpen(true)
                                                                            }}
                                                                        >
                                                                            <KTSVG path="/media/map/square-plus.svg" className="" />
                                                                        </button>
                                                                    )}
                                                                </>
                                                                )}
                                                                {actionGates.canDelete && (
                                                                    <button className="btn btn-sm px-0" onClick={() => { setIsDeleteVesselModalOpen(true); setSelectedVessel(vessel); }}>
                                                                        <KTSVG path='/media/map/trash.svg' className='' />
                                                                    </button>
                                                                )}
                                                                <button
                                                                  className="btn btn-sm px-0"
                                                                  title={getVesselDetailLabel(vessel.vesselType, 'view')}
                                                                  aria-label={getVesselDetailLabel(vessel.vesselType, 'view')}
                                                                  onClick={() => { setIsViewVesselModalOpen(true); setSelectedVessel(vessel); }}
                                                                >
                                                                    <KTSVG path='/media/map/ph_eye.svg' className='' />
                                                                </button>
                                                            </td>
                                                            {/* <td>
                                {infoExists[vessel.id] ? (
                                  <button
                                    className="btn btn-sm px-0"
                                    onClick={() => {
                                      setSelectedVessel(vessel)
                                      setIsEditMode(true)
                                      setIsAddAdditionalInfoModalOpen(true)
                                    }}
                                  >
                                    <KTSVG path="/media/map/edit-active.svg" className="" />
                                  </button>
                                ) : (
                                  <button
                                    className="btn btn-sm px-0"
                                    onClick={() => {
                                      setSelectedVessel(vessel)
                                      setIsEditMode(false)
                                      setIsAddAdditionalInfoModalOpen(true)
                                    }}
                                  >
                                    <KTSVG path="/media/map/square-plus.svg" className="" />
                                  </button>
                                )}
                              </td> */}
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan={showCompanyGroupColumn ? 7 : 6} className="text-center py-4 text-muted">
                                                            {adminListEmptyMessage(listAccess, 'No vessels found')}
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                        {/* Pagination */}
                                        <div className="pagination-wrapper d-flex justify-content-between align-items-center py-3" style={{ borderTop: "1px solid #dee2e6" }}>
                                            <div className="d-flex align-items-center">
                                                <span className="text-muted me-2">Rows per page</span>
                                                <SearchableSelect
                                                    className="form-select form-select-sm"
                                                    style={{
                                                        borderRadius: "20px",
                                                        width: "70px",
                                                        border: "1px solid #dee2e6",
                                                        fontSize: "14px",
                                                        padding: "4px 8px",
                                                    }}
                                                    value={rowsPerPage}
                                                    onChange={(e) => {
                                                        setRowsPerPage(parseInt(e.target.value));
                                                        setCurrentPage(1);
                                                    }}
                                                >
                                                    <option value={10}>10</option>
                                                    <option value={20}>20</option>
                                                    <option value={50}>50</option>
                                                    <option value={100}>100</option>
                                                </SearchableSelect>
                                            </div>

                                            <div className="d-flex align-items-center">
                                                <span className="text-muted me-3" style={{ fontSize: "14px" }}>
                                                    Showing <strong>{(currentPage - 1) * rowsPerPage + 1}-{Math.min(currentPage * rowsPerPage, filteredVessels.length)}</strong> of <strong>{filteredVessels.length}</strong>
                                                </span>

                                                <nav>
                                                    <ul className="pagination pagination-sm mb-0" style={{ gap: "2px" }}>
                                                        <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                                                            <button
                                                                className="page-link text-muted"
                                                                style={{
                                                                    backgroundColor: "#f8f9fa",
                                                                    border: "1px solid #dee2e6",
                                                                    padding: "8px 12px",
                                                                    fontSize: "14px",
                                                                    borderRadius: "6px",
                                                                }}
                                                                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                                                                disabled={currentPage === 1}
                                                            >
                                                                ‹
                                                            </button>
                                                        </li>

                                                        {(() => {
                                                            const pages = [];
                                                            const showPages = 5;
                                                            let startPage = Math.max(1, currentPage - 2);
                                                            let endPage = Math.min(totalPages, startPage + showPages - 1);

                                                            if (endPage - startPage < showPages - 1) {
                                                                startPage = Math.max(1, endPage - showPages + 1);
                                                            }

                                                            if (startPage > 1) {
                                                                pages.push(
                                                                    <li key={1} className="page-item">
                                                                        <button
                                                                            className="page-link text-muted"
                                                                            style={{
                                                                                backgroundColor: "#f8f9fa",
                                                                                border: "1px solid #dee2e6",
                                                                                padding: "8px 12px",
                                                                                fontSize: "14px",
                                                                                minWidth: "40px",
                                                                                borderRadius: "6px",
                                                                            }}
                                                                            onClick={() => setCurrentPage(1)}
                                                                        >
                                                                            1
                                                                        </button>
                                                                    </li>
                                                                );
                                                                if (startPage > 2) {
                                                                    pages.push(
                                                                        <li key="ellipsis1" className="page-item disabled">
                                                                            <span
                                                                                className="page-link border-0 text-muted"
                                                                                style={{ backgroundColor: "transparent", padding: "4px 8px" }}
                                                                            >
                                                                                ...
                                                                            </span>
                                                                        </li>
                                                                    );
                                                                }
                                                            }

                                                            for (let i = startPage; i <= endPage; i++) {
                                                                pages.push(
                                                                    <li key={i} className={`page-item ${currentPage === i ? "active" : ""}`}>
                                                                        <button
                                                                            className="page-link text-muted"
                                                                            style={{
                                                                                backgroundColor: currentPage === i ? "#F4F9FF" : "transparent",
                                                                                border: "1px solid #dee2e6",
                                                                                padding: "8px 12px",
                                                                                fontSize: "14px",
                                                                                minWidth: "40px",
                                                                                borderRadius: "6px",
                                                                                outline: "none",
                                                                                boxShadow: "none",
                                                                            }}
                                                                            onClick={() => setCurrentPage(i)}
                                                                        >
                                                                            {i}
                                                                        </button>
                                                                    </li>
                                                                );
                                                            }

                                                            if (endPage < totalPages) {
                                                                if (endPage < totalPages - 1) {
                                                                    pages.push(
                                                                        <li key="ellipsis2" className="page-item disabled">
                                                                            <span
                                                                                className="page-link border-0 text-muted"
                                                                                style={{ backgroundColor: "transparent", padding: "4px 8px" }}
                                                                            >
                                                                                ...
                                                                            </span>
                                                                        </li>
                                                                    );
                                                                }
                                                                pages.push(
                                                                    <li key={totalPages} className="page-item">
                                                                        <button
                                                                            className="page-link text-muted"
                                                                            style={{
                                                                                backgroundColor: "#f8f9fa",
                                                                                border: "1px solid #dee2e6",
                                                                                padding: "8px 12px",
                                                                                fontSize: "14px",
                                                                                minWidth: "40px",
                                                                                borderRadius: "6px",
                                                                            }}
                                                                            onClick={() => setCurrentPage(totalPages)}
                                                                        >
                                                                            {totalPages}
                                                                        </button>
                                                                    </li>
                                                                );
                                                            }

                                                            return pages;
                                                        })()}

                                                        <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                                                            <button
                                                                className="page-link text-muted"
                                                                style={{
                                                                    backgroundColor: "#f8f9fa",
                                                                    border: "1px solid #dee2e6",
                                                                    padding: "8px 12px",
                                                                    fontSize: "14px",
                                                                    borderRadius: "6px",
                                                                }}
                                                                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                                                                disabled={currentPage === totalPages}
                                                            >
                                                                ›
                                                            </button>
                                                        </li>
                                                    </ul>
                                                </nav>
                                            </div>
                                        </div>

                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                {isAddVesselModalOpen && (
                    <AddVesselModal isOpen={isAddVesselModalOpen}
                        onClose={() => { setIsAddVesselModalOpen(false) }}
                        onVesselAdded={fetchVessels}
                    />
                )}
                {isAddAdditionalInfoModalOpen && selectedVessel && (
                    getVesselDetailMode(selectedVessel.vesselType) === 'Q88' ? (
                      <AddAdditionalInfoModal
                          isOpen={isAddAdditionalInfoModalOpen}
                          isEdit={isEditMode}
                          onClose={() => setIsAddAdditionalInfoModalOpen(false)}
                          onVesselAdded={fetchVessels}
                          vesselData={selectedVessel}
                      />
                    ) : (
                      <ShipParticularsModal
                        isOpen={isAddAdditionalInfoModalOpen}
                        isEdit={isEditMode}
                        onClose={() => setIsAddAdditionalInfoModalOpen(false)}
                        onVesselAdded={fetchVessels}
                        vesselData={selectedVessel}
                      />
                    )
                )}
                {isDeleteVesselModalOpen && selectedVessel && (
                    <DeleteVesselModal
                        isOpen={isDeleteVesselModalOpen}
                        onClose={() => setIsDeleteVesselModalOpen(false)}
                        onVesselDeleted={fetchVessels}
                        vesselData={selectedVessel}
                    />
                )}
                {isEditVesselModalOpen && selectedVessel && (
                    <EditVesselModal
                        isOpen={isEditVesselModalOpen}
                        onClose={() => setIsEditVesselModalOpen(false)}
                        onVesselUpdated={fetchVessels}
                        vesselData={selectedVessel}
                    />
                )}
                {isViewVesselModalOpen && selectedVessel && (
                    getVesselDetailMode(selectedVessel.vesselType) === 'Q88' ? (
                      <ViewVesselModal
                          isOpen={isViewVesselModalOpen}
                          onClose={() => { setIsViewVesselModalOpen(false) }}
                          vesselData={selectedVessel}
                      />
                    ) : (
                      <ViewShipParticularsModal
                        isOpen={isViewVesselModalOpen}
                        onClose={() => { setIsViewVesselModalOpen(false) }}
                        vesselData={selectedVessel}
                      />
                    )
                )}

                {landingVessel && (
                    <VesselLandingModal
                        isOpen={!!landingVessel}
                        onClose={() => {
                            setLandingVesselId(null)
                            if (openedLandingFromQuery) {
                                setOpenedLandingFromQuery(false)
                                navigate('/manage/vessels', { replace: true })
                            }
                        }}
                        vesselData={landingVessel}
                    />
                )}
                
                <ToastContainer
                    position="top-center"
                    autoClose={3000}
                    hideProgressBar={false}
                    newestOnTop={false}
                    closeOnClick
                    rtl={false}
                    pauseOnFocusLoss
                    draggable
                    pauseOnHover
                    style={{ top: "5rem", zIndex: 99999 }}
                />
            </div>
        </div>
    )
}

export { VesselsList };
